import { useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useCompetition, useSetOpenForSignup } from '@/hooks/useCompetitions';
import {
  useApproveParticipant,
  useAssignParticipant,
  useParticipantsForCompetition,
  useRejectParticipant,
  useRemoveParticipant,
  useSetParticipantPaid,
} from '@/hooks/useCompetitionParticipants';
import { useUsersByIds } from '@/hooks/useUsers';
import { PageSpinner } from '@/components/ui/Spinner';
import { ErrorState, EmptyState, Alert } from '@/components/ui/Alert';
import { getApiErrorMessage } from '@/api/client';
import { Card, CardBody, CardHeader } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Table, type Column } from '@/components/ui/Table';
import { Badge } from '@/components/ui/Badge';
import { AssignUserForm } from '@/components/organizer/AssignUserForm';
import { ParticipantStatus, PaymentMethod } from '@/api/types';
import type { CompetitionParticipant } from '@/api/types';

/**
 * Per-competition competitor assignment. A competitor only sees/enters this
 * competition once they hold an Approved participant row - either
 * organizer-assigned directly (below, instantly Approved), or self-requested
 * via the "Request to join" button on CompetitionsListPage when the
 * competition is open for signup, then approved here.
 */
export function OrganizerParticipantsPage() {
  const { competitionId = '' } = useParams<{ competitionId: string }>();
  const competition = useCompetition(competitionId);
  const assignments = useParticipantsForCompetition(competitionId);
  const assignParticipant = useAssignParticipant(competitionId);
  const removeParticipant = useRemoveParticipant(competitionId);
  const approveParticipant = useApproveParticipant(competitionId);
  const rejectParticipant = useRejectParticipant(competitionId);
  const setPaid = useSetParticipantPaid(competitionId);
  const [copied, setCopied] = useState(false);
  const signupLink = `${window.location.origin}/razpis/${competitionId}`;
  const setOpenForSignup = useSetOpenForSignup(competitionId);

  const { usersById } = useUsersByIds((assignments.data?.items ?? []).map((a) => a.userId));

  const { pending, decided } = useMemo(() => {
    const items = assignments.data?.items ?? [];
    return {
      pending: items.filter((a) => a.status === ParticipantStatus.Pending),
      decided: items.filter((a) => a.status !== ParticipantStatus.Pending),
    };
  }, [assignments.data]);

  function describeUser(userId: string): string {
    const u = usersById.get(userId);
    if (!u) return userId;
    return u.displayName || `${u.firstName ?? ''} ${u.lastName ?? ''}`.trim() || u.email;
  }

  function statusBadge(status: ParticipantStatus) {
    if (status === ParticipantStatus.Approved) return <Badge tone="Accepted">Potrjen</Badge>;
    if (status === ParticipantStatus.Rejected) return <Badge tone="Rejected">Zavrnjen</Badge>;
    return <Badge tone="UnderReview">Čaka</Badge>;
  }

  const decidedColumns: Column<CompetitionParticipant>[] = [
    { header: 'Tekmovalec', accessor: (a) => describeUser(a.userId) },
    { header: 'E-pošta', accessor: (a) => usersById.get(a.userId)?.email ?? '—' },
    { header: 'Status', accessor: (a) => statusBadge(a.status) },
    { header: 'Dodano', accessor: (a) => new Date(a.invitedAt).toLocaleDateString() },
    {
      header: 'Plačilo',
      accessor: (a) => (
        <div className="flex flex-col gap-0.5">
          <select
            className="h-9 rounded-control border border-line-strong bg-surface-0 px-2 text-sm text-fg"
            aria-label={`Plačilo: ${describeUser(a.userId)}`}
            value={a.hasPaid ? String(a.paidVia ?? PaymentMethod.BankTransfer) : ''}
            disabled={setPaid.isPending && setPaid.variables?.participantId === a.id}
            onChange={(e) =>
              setPaid.mutate(
                e.target.value === ''
                  ? { participantId: a.id, hasPaid: false }
                  : { participantId: a.id, hasPaid: true, method: Number(e.target.value) as PaymentMethod },
              )
            }
          >
            <option value="">Ni plačano</option>
            <option value={PaymentMethod.BankTransfer}>Plačano: bančno nakazilo</option>
            <option value={PaymentMethod.Card}>Plačano: kartica</option>
          </select>
          {a.hasPaid && a.paidAt && <span className="text-xs text-fg-muted">{new Date(a.paidAt).toLocaleDateString()}</span>}
        </div>
      ),
    },
    {
      header: 'Dejanja',
      accessor: (a) => (
        <Button
          variant="danger"
          size="sm"
          isLoading={removeParticipant.isPending && removeParticipant.variables === a.id}
          onClick={() => {
            if (confirm(`Odstranite "${describeUser(a.userId)}" s tega tekmovanja?`)) {
              removeParticipant.mutate(a.id);
            }
          }}
        >
          Odstrani
        </Button>
      ),
    },
  ];

  return (
    <div className="flex flex-col gap-4">
      <div>
        <Link to="/organizer/competitions" className="text-sm text-fg-muted hover:underline">
          &larr; Nazaj na tekmovanja
        </Link>
        <h1 className="text-xl font-semibold text-fg">
          Tekmovalci {competition.data ? `za "${competition.data.name}"` : ''}
        </h1>
        <p className="text-sm text-fg-muted">
          Tekmovalci se prijavijo sami prek povezave na razpis in lahko takoj oddajajo. Lahko jih dodate tudi po e-pošti, odstranite in označite, kdo je že plačal.
        </p>
      </div>

      <Card>
        <CardHeader className="flex flex-row items-center justify-between gap-2">
          <div>
            <h2 className="font-semibold text-fg">Prijava tekmovalcev</h2>
            <p className="text-sm text-fg-muted">
              Ko so prijave odprte, se tekmovalci sami prijavijo prek povezave na razpis (brez potrjevanja).
            </p>
            <p className="mt-1 flex flex-wrap items-center gap-2 text-sm">
              <code className="rounded-control bg-surface-2 px-2 py-1 text-fg-secondary">{signupLink}</code>
              <Button
                variant="secondary"
                size="sm"
                onClick={() => {
                  void navigator.clipboard?.writeText(signupLink).then(() => setCopied(true));
                }}
              >
                {copied ? 'Kopirano' : 'Kopiraj povezavo'}
              </Button>
            </p>
          </div>
          <Button
            variant={competition.data?.isOpenForSignup ? 'danger' : 'secondary'}
            size="sm"
            isLoading={setOpenForSignup.isPending}
            disabled={!competition.data}
            onClick={() => setOpenForSignup.mutate(!competition.data?.isOpenForSignup)}
          >
            {competition.data?.isOpenForSignup ? 'Zapri prijave' : 'Odpri prijave'}
          </Button>
        </CardHeader>
      </Card>

      <Card>
        <CardBody>
          <AssignUserForm
            actionLabel="Dodeli tekmovalca"
            isPending={assignParticipant.isPending}
            error={assignParticipant.error}
            onAssign={(payload) => assignParticipant.mutateAsync(payload)}
          />
        </CardBody>
      </Card>

      {assignments.isLoading && <PageSpinner />}
      {assignments.isError && (
        <ErrorState message={getApiErrorMessage(assignments.error)} onRetry={() => assignments.refetch()} />
      )}
      {(removeParticipant.isError || approveParticipant.isError || rejectParticipant.isError || setPaid.isError) && (
        <Alert variant="error">
          {getApiErrorMessage(removeParticipant.error ?? approveParticipant.error ?? rejectParticipant.error ?? setPaid.error)}
        </Alert>
      )}

      {pending.length > 0 && (
        <Card>
          <CardHeader>
            <h2 className="font-semibold text-fg">Prošnje za sodelovanje ({pending.length})</h2>
          </CardHeader>
          <CardBody>
            <ul className="flex flex-col gap-2">
              {pending.map((a) => (
                <li
                  key={a.id}
                  className="flex items-center justify-between gap-2 rounded-control border border-line px-3 py-2"
                >
                  <span className="text-sm text-fg">
                    {describeUser(a.userId)}{' '}
                    <span className="text-fg-muted">({usersById.get(a.userId)?.email ?? a.userId})</span>
                  </span>
                  <div className="flex gap-2">
                    <Button
                      size="sm"
                      isLoading={approveParticipant.isPending && approveParticipant.variables === a.id}
                      onClick={() => approveParticipant.mutate(a.id)}
                    >
                      Potrdi
                    </Button>
                    <Button
                      variant="danger"
                      size="sm"
                      isLoading={rejectParticipant.isPending && rejectParticipant.variables === a.id}
                      onClick={() => rejectParticipant.mutate(a.id)}
                    >
                      Zavrni
                    </Button>
                  </div>
                </li>
              ))}
            </ul>
          </CardBody>
        </Card>
      )}

      {assignments.isSuccess && decided.length === 0 && (
        <EmptyState title="Še ni dodeljenih tekmovalcev" description="Zgoraj dodelite tekmovalca." />
      )}
      {assignments.isSuccess && decided.length > 0 && (
        <>
          <p className="text-sm text-fg-muted">
            Plačalo {decided.filter((a) => a.hasPaid).length} od {decided.length}
          </p>
          <Table columns={decidedColumns} rows={decided} getRowKey={(a) => a.id} />
        </>
      )}
    </div>
  );
}
